import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, MapPin, Briefcase, Clock, Filter, X, ChevronRight, 
  Building2, Code2, Users, Sparkles 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Active filters
  const [activeFilters, setActiveFilters] = useState({
    location: '',
    designation: '',
    technology: '',
    experience: null
  });

  useEffect(() => {
    fetchJobs();
    fetchFilters();
  }, []);

  const fetchJobs = async () => {
    try {
      let url = `${API_URL}/api/careers/jobs?active_only=true`;
      
      if (activeFilters.location) url += `&location=${activeFilters.location}`;
      if (activeFilters.designation) url += `&designation=${activeFilters.designation}`;
      if (activeFilters.technology) url += `&technology=${activeFilters.technology}`;
      if (activeFilters.experience !== null) url += `&experience=${activeFilters.experience}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careers/filters`);
      if (response.ok) {
        const data = await response.json();
        setFilters(data);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [activeFilters]);

  const handleFilterChange = (key, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [key]: value === prev[key] ? '' : value
    }));
  };

  const clearFilters = () => {
    setActiveFilters({
      location: '',
      designation: '',
      technology: '',
      experience: null
    });
    setSearchTerm('');
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      job.title.toLowerCase().includes(search) ||
      job.designation.toLowerCase().includes(search) ||
      job.location.toLowerCase().includes(search) ||
      job.technologies.some(t => t.toLowerCase().includes(search))
    );
  });

  const hasActiveFilters = activeFilters.location || activeFilters.designation || 
                           activeFilters.technology || activeFilters.experience !== null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      <SEO 
        title="Careers - Join Our Team | Navaris Digital"
        description="Explore exciting career opportunities at Navaris Digital. Join our team of innovators and build the future of technology."
        canonicalUrl="/careers"
      />
      
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center pt-20 overflow-hidden bg-gradient-to-br from-[#320547] via-[#4a1a6b] to-[#95228A]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-white/70 text-sm font-semibold uppercase tracking-widest mb-4">
              Join Our Team
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="careers-hero-title">
              Build Your Career With Us
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Join a team of passionate innovators working on cutting-edge technology solutions. 
              We're always looking for talented individuals to help us shape the future.
            </p>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-6 mt-8">
              <div className="flex items-center gap-2 text-white/90">
                <Briefcase size={20} className="text-[#f0c674]" />
                <span className="font-semibold">{jobs.length} Open Positions</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <MapPin size={20} className="text-[#f0c674]" />
                <span className="font-semibold">{filters?.locations?.length || 0} Locations</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Users size={20} className="text-[#f0c674]" />
                <span className="font-semibold">Growing Team</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 border-b border-gray-100 sticky top-16 bg-white z-40 shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search jobs by title, technology, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full"
                data-testid="job-search-input"
              />
            </div>
            
            {/* Filter Toggle */}
            <div className="flex items-center gap-3">
              <Button
                variant={showFilters ? "default" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
                data-testid="filter-toggle-btn"
              >
                <Filter size={18} />
                Filters
                {hasActiveFilters && (
                  <span className="bg-[#95228A] text-white text-xs px-2 py-0.5 rounded-full">
                    Active
                  </span>
                )}
              </Button>
              
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="text-gray-500">
                  <X size={18} className="mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {/* Filter Panel */}
          {showFilters && filters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6 p-6 bg-gray-50 rounded-xl"
              data-testid="filter-panel"
            >
              {/* Location Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                <div className="flex flex-wrap gap-2">
                  {filters.locations?.map(loc => (
                    <button
                      key={loc}
                      onClick={() => handleFilterChange('location', loc)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        activeFilters.location === loc
                          ? 'bg-[#320547] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                      data-testid={`filter-location-${loc}`}
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Designation Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Designation</label>
                <div className="flex flex-wrap gap-2">
                  {filters.designations?.map(des => (
                    <button
                      key={des}
                      onClick={() => handleFilterChange('designation', des)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        activeFilters.designation === des
                          ? 'bg-[#320547] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                      data-testid={`filter-designation-${des}`}
                    >
                      {des}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Technology Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Technology</label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {filters.technologies?.slice(0, 10).map(tech => (
                    <button
                      key={tech}
                      onClick={() => handleFilterChange('technology', tech)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        activeFilters.technology === tech
                          ? 'bg-[#320547] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                      data-testid={`filter-tech-${tech}`}
                    >
                      {tech}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Experience Filter */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Experience</label>
                <div className="flex flex-wrap gap-2">
                  {filters.experience_ranges?.map(range => (
                    <button
                      key={range.label}
                      onClick={() => handleFilterChange('experience', range.min)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                        activeFilters.experience === range.min
                          ? 'bg-[#320547] text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                      data-testid={`filter-exp-${range.label}`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          {filteredJobs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Sparkles size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No positions found</h3>
              <p className="text-gray-500 mb-6">
                {hasActiveFilters || searchTerm 
                  ? "Try adjusting your filters or search terms"
                  : "We don't have any open positions at the moment. Check back soon!"}
              </p>
              {(hasActiveFilters || searchTerm) && (
                <Button onClick={clearFilters} variant="outline">
                  Clear all filters
                </Button>
              )}
            </motion.div>
          ) : (
            <div className="grid gap-4" data-testid="job-listings">
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/careers/${job.slug}`}
                    className="block group"
                    data-testid={`job-card-${job.slug}`}
                  >
                    <div className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-lg hover:border-[#320547]/20 transition-all duration-300">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-[#320547] group-hover:text-[#95228A] transition-colors">
                              {job.title}
                            </h3>
                            {job.employment_type && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-[#320547]/10 text-[#320547] rounded">
                                {job.employment_type}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-[#95228A] font-medium mb-3">{job.designation}</p>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <MapPin size={16} />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={16} />
                              {job.experience_min}-{job.experience_max} years
                            </span>
                            {job.technologies?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Code2 size={16} />
                                {job.technologies.slice(0, 3).join(', ')}
                                {job.technologies.length > 3 && ` +${job.technologies.length - 3}`}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <span className="text-[#320547] font-semibold group-hover:translate-x-1 transition-transform flex items-center gap-1">
                            View Details
                            <ChevronRight size={18} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#320547] mb-4">Why Join Navaris Digital?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're more than just a company - we're a community of innovators passionate about technology and growth.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Sparkles,
                title: "Innovation First",
                description: "Work on cutting-edge projects using the latest technologies and methodologies."
              },
              {
                icon: Users,
                title: "Great Culture",
                description: "Collaborative environment where every voice matters and ideas are valued."
              },
              {
                icon: Building2,
                title: "Growth Opportunities",
                description: "Clear career paths, learning opportunities, and professional development."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <item.icon size={32} className="text-[#95228A] mb-4" />
                <h3 className="text-xl font-semibold text-[#320547] mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
